import { LOCALES, LOCALE_LABEL, getLocale, onLocaleChange, setLocale, t } from "../i18n/index.js";
import { isLowBandwidth, setLowBandwidth } from "../settings.js";
import { el } from "./dom.js";

/**
 * Language and low-bandwidth mode.
 *
 * Both live behind one button so the venue keeps a single settings affordance
 * rather than sprouting toggles along the edge of a phone screen.
 */
export class SettingsPanel {
  private readonly button: HTMLButtonElement;
  private readonly panel: HTMLDivElement;
  private readonly title: HTMLHeadingElement;
  private readonly languageLabel: HTMLParagraphElement;
  private readonly languageRow: HTMLDivElement;
  private readonly bandwidthLabel: HTMLSpanElement;
  private readonly bandwidthHelp: HTMLSpanElement;
  private readonly bandwidthToggle: HTMLButtonElement;
  private readonly closeButton: HTMLButtonElement;
  private readonly unsubscribe: () => void;

  constructor(parent: HTMLElement) {
    this.button = el("button", {
      className: "zg-settings-button",
      text: "⚙",
      attrs: { type: "button" },
    });
    this.button.addEventListener("click", () => this.toggle());

    this.title = el("h2", { className: "zg-title" });
    this.languageLabel = el("p", { className: "zg-label" });
    this.languageRow = el("div", { className: "zg-lang-row", attrs: { role: "radiogroup" } });

    for (const locale of LOCALES) {
      const option = el("button", {
        className: "zg-lang",
        text: LOCALE_LABEL[locale],
        attrs: { type: "button", role: "radio", "data-locale": locale },
      });
      option.addEventListener("click", () => setLocale(locale));
      this.languageRow.appendChild(option);
    }

    this.bandwidthLabel = el("span", { className: "zg-toggle-label" });
    this.bandwidthHelp = el("span", { className: "zg-toggle-help" });
    this.bandwidthToggle = el("button", {
      className: "zg-toggle",
      attrs: { type: "button", role: "switch" },
      children: [
        el("span", {
          className: "zg-toggle-text",
          children: [this.bandwidthLabel, this.bandwidthHelp],
        }),
        el("span", { className: "zg-toggle-switch" }),
      ],
    });
    this.bandwidthToggle.addEventListener("click", () => {
      setLowBandwidth(!isLowBandwidth());
      this.applyState();
    });

    this.closeButton = el("button", { className: "zg-button", attrs: { type: "button" } });
    this.closeButton.addEventListener("click", () => this.close());

    this.panel = el("div", {
      className: "zg-modal zg-settings",
      children: [
        el("div", {
          className: "zg-card",
          children: [
            this.title,
            this.languageLabel,
            this.languageRow,
            this.bandwidthToggle,
            this.closeButton,
          ],
        }),
      ],
    });
    this.panel.addEventListener("click", (event) => {
      if (event.target === this.panel) this.close();
    });

    parent.appendChild(this.button);
    parent.appendChild(this.panel);

    this.applyStrings();
    this.applyState();
    this.unsubscribe = onLocaleChange(() => {
      this.applyStrings();
      this.applyState();
    });
  }

  private applyStrings(): void {
    this.button.setAttribute("aria-label", t("settings.open"));
    this.title.textContent = t("settings.title");
    this.languageLabel.textContent = t("settings.language");
    this.bandwidthLabel.textContent = t("settings.lowBandwidth");
    this.bandwidthHelp.textContent = t("settings.lowBandwidthHelp");
    this.closeButton.textContent = t("settings.close");
  }

  private applyState(): void {
    const active = getLocale();
    this.languageRow.querySelectorAll<HTMLElement>(".zg-lang").forEach((node) => {
      const selected = node.dataset.locale === active;
      node.classList.toggle("is-selected", selected);
      node.setAttribute("aria-checked", selected ? "true" : "false");
    });

    const low = isLowBandwidth();
    this.bandwidthToggle.classList.toggle("is-on", low);
    this.bandwidthToggle.setAttribute("aria-checked", low ? "true" : "false");
  }

  setVisible(visible: boolean): void {
    this.button.classList.toggle("is-visible", visible);
  }

  private toggle(): void {
    if (this.panel.classList.contains("is-open")) this.close();
    else this.open();
  }

  open(): void {
    this.applyState();
    this.panel.classList.add("is-open");
  }

  close(): void {
    this.panel.classList.remove("is-open");
  }

  destroy(): void {
    this.unsubscribe();
    this.button.remove();
    this.panel.remove();
  }
}
